import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Stack,
  Alert,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useIntl } from "react-intl";
import { useAuth } from "../../providers/AuthProvider";
import { useNotification } from "../../providers/NotificationProvider";
import DraggablePaper from "../../components/common/DraggablePaper";
import {
  fetchActionPlan,
  fetchAction,
  createWork,
  startWork,
  fetchTodoWorks,
  fetchDoingWorks,
  fetchDoneWorks,
} from "../../api/memberApi";

// 言語設定に応じて姓名を結合する（元の仕様と統一）
const buildMemberName = (member) => {
  if (!member) return "";
  const last = member.lastName || "";
  const first = member.firstName || "";
  if (navigator.language === "ja") {
    return `${last} ${first}`.trim();
  }
  return `${first} ${last}`.trim();
};

export default function AddWorkDialog({
  open,
  onClose,
  onCreated,
  workflowId,
  workerId,
  actionPlanId,
}) {
  const intl = useIntl();
  const { companyId, memberId, member } = useAuth();
  const notify = useNotification();

  const [actions, setActions] = useState([]);
  const [selectedActionId, setSelectedActionId] = useState("");
  const [actionDetail, setActionDetail] = useState(null);
  const [workName, setWorkName] = useState("");
  const [workDescription, setWorkDescription] = useState("");
  const [loadingActions, setLoadingActions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 作業モード: "todo" = ToDo登録のみ / "doing" = 登録後すぐ実行中
  const [workMode, setWorkMode] = useState("todo");

  // バリデーションエラー
  const [actionError, setActionError] = useState(false);
  const [workNameError, setWorkNameError] = useState(false);

  // アクションプラン読み込み
  useEffect(() => {
    if (!open) return;
    console.log("[AddWorkDialog] open, actionPlanId =", actionPlanId);
    if (!actionPlanId) {
      setActions([]);
      return;
    }
    setLoadingActions(true);
    setSelectedActionId("");
    setActionDetail(null);
    fetchActionPlan(actionPlanId)
      .then((data) => {
        console.log("[AddWorkDialog] fetchActionPlan response =", data);
        // バックエンドは data.associatedActions を返す
        const list = data?.associatedActions ?? data?.actions ?? [];
        console.log("[AddWorkDialog] actions list =", list);
        setActions(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error("[AddWorkDialog] fetchActionPlan error =", err);
        setActions([]);
      })
      .finally(() => setLoadingActions(false));
  }, [open, actionPlanId]);

  // アクション選択時に詳細取得（applicationUrl / personUrl）＋ 作業名自動入力（連番付き）
  const handleActionChange = async (actionId) => {
    setSelectedActionId(actionId);
    setActionError(false);
    setActionDetail(null);
    if (!actionId) return;

    // 選択されたアクションの名前を取得して作業名を自動設定（連番採番）
    const selected = actions.find((a) => a.actionId === actionId);
    if (selected?.actionName) {
      const today = new Date();
      const yyyymmdd =
        `${today.getFullYear()}` +
        `${String(today.getMonth() + 1).padStart(2, "0")}` +
        `${String(today.getDate()).padStart(2, "0")}`;
      const baseName = `${selected.actionName}_${yyyymmdd}`;

      // 同ワークフロー内の全ワーク（Todo/Doing/Done）を取得して同プレフィックスをカウント
      let count = 0;
      try {
        const [todos, doings, dones] = await Promise.all([
          fetchTodoWorks(workflowId).catch(() => []),
          fetchDoingWorks(workflowId).catch(() => []),
          fetchDoneWorks(workflowId).catch(() => []),
        ]);
        const allWorks = [...(todos || []), ...(doings || []), ...(dones || [])];
        count = allWorks.filter((w) => {
          const name = w.workName || "";
          // baseName と完全一致、または baseName_数字 の形式
          return name === baseName || new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}_\\d+$`).test(name);
        }).length;
      } catch {
        // カウント取得失敗時は 1 から開始
      }

      setWorkName(`${baseName}_${count + 1}`);
      setWorkNameError(false);
    }

    try {
      const action = await fetchAction(actionId);
      setActionDetail(action);
    } catch {
      // URL 情報なしで続行
    }
  };

  // バリデーション
  const validate = () => {
    let valid = true;
    if (!workName.trim()) {
      setWorkNameError(true);
      valid = false;
    }
    // actionId は必須
    if (!selectedActionId) {
      setActionError(true);
      valid = false;
    }
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const memberName = buildMemberName(member);
      const workBody = {
        companyId,
        actionId: selectedActionId,
        memberId,
        memberName,
        workflowId,
        workName: workName.trim(),
        workDescription: workDescription.trim(),
        workerId,
      };
      // URL は存在する場合のみ送る（元の仕様に合わせる）
      if (actionDetail?.applicationUrl) workBody.applicationUrl = actionDetail.applicationUrl;
      if (actionDetail?.personUrl) workBody.personUrl = actionDetail.personUrl;

      const result = await createWork(workBody);

      if (workMode === "doing") {
        // 作業実行モード：登録後すぐに Doing に移行
        const newWorkId = result?.returnValue;
        if (newWorkId) {
          await startWork({
            workId: newWorkId,
            companyId,
            actionId: selectedActionId,
            memberId,
            memberName,
            workflowId,
            workName: workName.trim(),
            workDescription: workDescription.trim(),
            applicationUrl: actionDetail?.applicationUrl || "",
            personUrl: actionDetail?.personUrl || "",
            startDate: new Date().toISOString(),
          });
        }
        notify.success(intl.formatMessage({ id: "work.executeSuccess" }));
      } else {
        notify.success(intl.formatMessage({ id: "work.createSuccess" }));
      }

      handleClose();
      onCreated?.();
    } catch (err) {
      notify.apiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedActionId("");
    setActionDetail(null);
    setWorkName("");
    setWorkDescription("");
    setActionError(false);
    setWorkNameError(false);
    setWorkMode("todo");
    onClose();
  };

  // 登録ボタンの disabled 条件
  // actionId は必須のため、常に selectedActionId が設定されていることを要求する
  const cannotSubmit =
    submitting ||
    !workName.trim() ||
    loadingActions ||
    !selectedActionId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
      <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{intl.formatMessage({ id: "work.add.title" })}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* 作業モード選択 */}
          <Stack spacing={0.5}>
            <ToggleButtonGroup
              value={workMode}
              exclusive
              onChange={(_, val) => { if (val) setWorkMode(val); }}
              size="small"
              fullWidth
            >
              <ToggleButton value="todo">
                {intl.formatMessage({ id: "work.add.mode.todo" })}
              </ToggleButton>
              <ToggleButton value="doing">
                {intl.formatMessage({ id: "work.add.mode.doing" })}
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              {intl.formatMessage({ id: workMode === "todo" ? "work.add.mode.todo.hint" : "work.add.mode.doing.hint" })}
            </Typography>
          </Stack>

          {/* actionPlanId なし: 作業追加不可 */}
          {!actionPlanId && (
            <Alert severity="error" sx={{ fontSize: "0.8rem" }}>
              {intl.formatMessage({ id: "work.add.noActionPlan" })}
            </Alert>
          )}
          {/* actionPlanId あり・アクション0件 */}
          {actionPlanId && !loadingActions && actions.length === 0 && (
            <Alert severity="warning" sx={{ fontSize: "0.8rem" }}>
              {intl.formatMessage({ id: "work.add.noActions" })}
            </Alert>
          )}

          {/* アクション選択（actionPlanId がある場合は常に表示） */}
          {actionPlanId && (
            <FormControl
              fullWidth
              size="small"
              required
              error={actionError}
            >
              <InputLabel>
                {intl.formatMessage({ id: "work.action" })} *
              </InputLabel>
              {loadingActions ? (
                <Select value="" label={`${intl.formatMessage({ id: "work.action" })} *`} disabled>
                  <MenuItem value="">
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    {intl.formatMessage({ id: "common.loading" })}
                  </MenuItem>
                </Select>
              ) : (
                <Select
                  value={selectedActionId}
                  label={`${intl.formatMessage({ id: "work.action" })} *`}
                  onChange={(e) => handleActionChange(e.target.value)}
                >
                  <MenuItem value="" disabled>
                    {intl.formatMessage({ id: "work.action.placeholder" })}
                  </MenuItem>
                  {actions.map((a) => (
                    <MenuItem key={a.actionId} value={a.actionId}>
                      {a.actionName}
                    </MenuItem>
                  ))}
                </Select>
              )}
              {actionError && (
                <FormHelperText>
                  {intl.formatMessage({ id: "work.action.required" })}
                </FormHelperText>
              )}
            </FormControl>
          )}

          {/* 作業名 */}
          <TextField
            label={intl.formatMessage({ id: "work.name" })}
            value={workName}
            onChange={(e) => {
              setWorkName(e.target.value);
              if (e.target.value.trim()) setWorkNameError(false);
            }}
            size="small"
            fullWidth
            required
            autoFocus
            error={workNameError}
            helperText={
              workNameError
                ? intl.formatMessage({ id: "work.name.required" })
                : ""
            }
          />

          {/* 作業説明 */}
          <Typography variant="caption" color="text.secondary">
            {intl.formatMessage({ id: "work.description.hint" })}
          </Typography>
          <TextField
            label={intl.formatMessage({ id: "work.description" })}
            value={workDescription}
            onChange={(e) => setWorkDescription(e.target.value)}
            multiline
            rows={3}
            size="small"
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {intl.formatMessage({ id: "common.cancel" })}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={cannotSubmit}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            intl.formatMessage({ id: workMode === "doing" ? "work.add.mode.doing" : "common.create" })
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
